// src/ThemeContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// 🆕 Construit une clé de stockage unique par utilisateur
const getThemeKey = () => {
    try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            const id = user?.email || user?._id || user?.username;
            if (id) return `theme_${id}`;
        }
    } catch (_) { /* ignore */ }
    return 'theme_guest';
};

// 🆕 Lit le thème stocké pour l'utilisateur courant
const readStoredTheme = () => {
    try {
        const key = getThemeKey();
        const saved = localStorage.getItem(key);
        if (saved === 'dark' || saved === 'light') return saved;
        // Fallback : préférence système
        if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
    } catch (_) { /* ignore */ }
    return 'light';
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => readStoredTheme());

    // 🆕 Écoute les changements d'utilisateur (login/logout) → recharge le bon thème
    useEffect(() => {
        const handleUserChange = () => {
            const newTheme = readStoredTheme();
            setTheme(newTheme);
        };

        // Événement custom émis par Login/Navbar
        window.addEventListener('user:changed', handleUserChange);
        // Événement natif quand un autre onglet modifie localStorage
        window.addEventListener('storage', handleUserChange);

        return () => {
            window.removeEventListener('user:changed', handleUserChange);
            window.removeEventListener('storage', handleUserChange);
        };
    }, []);

    // Applique le thème au DOM + le sauvegarde pour l'user courant
    useEffect(() => {
        const body = document.body;
        const html = document.documentElement;

        body.classList.remove('light-theme', 'dark-theme', 'light-mode', 'dark-mode');
        html.classList.remove('light-theme', 'dark-theme', 'light-mode', 'dark-mode');

        if (theme === 'dark') {
            body.classList.add('dark-theme', 'dark-mode');
            html.classList.add('dark-theme', 'dark-mode');
            body.style.backgroundColor = '#121212';
            body.style.color = '#ffffff';
            html.style.backgroundColor = '#121212';
            html.style.color = '#ffffff';
        } else {
            body.classList.add('light-theme', 'light-mode');
            html.classList.add('light-theme', 'light-mode');
            body.style.backgroundColor = '#ffffff';
            body.style.color = '#000000';
            html.style.backgroundColor = '#ffffff';
            html.style.color = '#000000';
        }

        // 🆕 Sauvegarde par utilisateur (pas globale)
        try {
            const key = getThemeKey();
            localStorage.setItem(key, theme);
        } catch (_) { /* ignore */ }

        console.log(`[Theme] Appliqué: ${theme} (clé: ${getThemeKey()})`);
    }, [theme]);

    const toggleTheme = () => {
        setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
    };

    const value = {
        theme,
        toggleTheme,
        isDark: theme === 'dark',
        isLight: theme === 'light',
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};